import React, { forwardRef } from 'react';
import { Checkbox as MuiCheckbox, FormControlLabel, FormControl } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export interface CheckboxProps {
  /** Whether the checkbox is checked */
  checked?: boolean;
  /** Change handler */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Blur handler */
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  /** Field name */
  name?: string;
  /** Label text or rich content (e.g. JSX with links) */
  label: React.ReactNode;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Additional input props */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** Test ID for automated testing */
  testId?: string;
  /** Whether the field is in an error state — turns the checkbox red */
  error?: boolean;
  /** Optional style overrides for FormControlLabel wrapper */
  formControlLabelSx?: SxProps<Theme>;
}

/**
 * Checkbox - A checkbox input with label
 * 
 * Atomic Level: Atom
 * 
 * A simple wrapper around Material-UI Checkbox that provides a consistent
 * interface for checkbox inputs across the application.
 * 
 * @example
 * <Checkbox
 *   checked={checked}
 *   onChange={handleChange}
 *   label="I agree to the terms"
 * />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      onChange,
      onBlur,
      name,
      label,
      disabled,
      inputProps,
      testId,
      error,
      formControlLabelSx,
    },
    ref
  ) => {
    return (
      <FormControl error={error} component="fieldset">
        <FormControlLabel
          control={
            <MuiCheckbox
              inputRef={ref}
              checked={checked}
              onChange={onChange}
              onBlur={onBlur}
              name={name}
              disabled={disabled}
              sx={error ? { color: 'error.main' } : undefined}
              inputProps={{
                'data-testid': testId,
                ...inputProps,
              } as React.InputHTMLAttributes<HTMLInputElement>}
            />
          }
          label={label}
          sx={formControlLabelSx}
        />
      </FormControl>
    );
  }
);

Checkbox.displayName = 'Checkbox';
